import React, { useEffect, useState } from 'react'; 
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE } from '../apiConfig';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Don't show search in header on search results page
  const showSearch = !location.pathname.includes('/search-results') && !location.pathname.includes('/search');

  // Initialize Bootstrap's JavaScript functionality
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      if (!window.bootstrap) {
        const bootstrapJS = document.createElement('script');
        bootstrapJS.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/js/bootstrap.bundle.min.js';
        bootstrapJS.integrity = 'sha384-ka7Sk0Gln4gmtz2MlQnikT1wXgYsOg+OMhuP+IlRH9sENBO0LRn5q+8nbTov4+1p';
        bootstrapJS.crossOrigin = 'anonymous';
        document.body.appendChild(bootstrapJS);
      }
    }
  }, []);

  // Fetch product suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/products/search?q=${searchQuery}&limit=5`);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching search suggestions:', error);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(searchQuery)}&page=1`);
      setShowSuggestions(false);
    }
  };

  return (
    <nav className="navbar navbar-light bg-light shadow-sm">
      <div className="container">
        {/* Logo and brand */}
        <Link className="navbar-brand d-flex align-items-center gap-2 mb-0 text-decoration-none" to="/">
          <img
            src="https://i.imgur.com/SPB6PMM.png"
            alt="StoreToYou Logo"
            onError={(event) => { event.currentTarget.style.display = 'none'; }}
            style={{ height: "50px", width: "auto" }}
          />
          <span
            className="fw-semibold text-nowrap"
            style={{ color: '#FF385C', fontSize: '1.25rem', lineHeight: 1 }}
          >
            StoreToYou
          </span>
        </Link>

        {/* Navigation items - always visible */}
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center w-100">
          {/* Main nav links */}
          <ul className="navbar-nav me-auto d-flex flex-row gap-3 mb-2 mb-lg-0">
            <li className="nav-item">
              <Link className="nav-link px-0 px-lg-2" to="/">Home</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link px-0 px-lg-2" to="/city/kelowna">Kelowna Stores</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link px-0 px-lg-2" to="/dashboard">Dashboard</Link>
            </li>
          </ul>

          {/* Right side items */}
          <div className="d-flex ms-auto align-items-center" style={{ maxWidth: '100%' }}>
            {/* Search bar or search icon */}
            {showSearch ? (
              <div className="search-container position-relative" style={{ width: '250px', minWidth: 0, flexShrink: 1, marginRight: '15px' }}>
                <form onSubmit={handleSearchSubmit} className="d-flex">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search for products, store"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="submit"
                      aria-label="Search"
                    >
                      <FaSearch />
                    </button>
                  </div>
                </form>

                {/* Product suggestions dropdown */}
                {showSuggestions && searchQuery.length >= 2 && suggestions.length > 0 && (
                  <div className="position-absolute bg-white shadow-sm w-100 mt-1 rounded border z-3">
                    {suggestions.map(product => (
                      <Link 
                        key={product.id} 
                        to={`/${product.city}/${product.store_url_name}/product/${product.id}`}
                        className="d-flex align-items-center p-2 text-decoration-none text-dark border-bottom"
                        onClick={() => {
                          setShowSuggestions(false);
                          setSearchQuery('');
                        }}
                      >
                        {product.image_url && (
                          <img 
                            src={product.image_url} 
                            alt={product.name} 
                            className="me-2" 
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }} 
                          />
                        )}
                        <div>
                          <div className="fw-bold">{product.name}</div>
                          <small className="text-muted">${product.price} • {product.store_name}</small>
                        </div>
                      </Link>
                    ))}
                    
                    {/* View all results link */}
                    <Link 
                      to={`/search-results?q=${encodeURIComponent(searchQuery)}&page=1`}
                      className="d-block p-2 text-center text-primary"
                      onClick={() => setShowSuggestions(false)}
                    >
                      View all results for "{searchQuery}"
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/search-results" className="nav-link me-3">
                <FaSearch size={20} />
              </Link>
            )}

            {/* Auth links */}
            <div className="d-flex">
              {token ? (
                <button
                  className="nav-link btn btn-link px-0 px-lg-2"
                  style={{ textDecoration: 'none' }}
                  onClick={handleLogout}
                >
                  Logout
                </button>
              ) : (
                <>
                  <Link className="nav-link px-0 px-lg-2 me-3" to="/register">Register</Link>
                  <Link className="nav-link px-0 px-lg-2" to="/login">Login</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
